import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useRequestActivityQuery } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { Button, SkeletonLoader, Text } from '@frontend/design-system';
import { ActivityRow } from './components/activity-row';

type PaymentRequestActivityProps = {
  invoiceId: string;
  locationId: string;
  compactView?: boolean;
  userChipAtBottom?: boolean;
  maxVisibleActivities?: number;
};
export const PaymentRequestActivity = ({
  invoiceId,
  locationId,
  compactView,
  userChipAtBottom,
  maxVisibleActivities,
}: PaymentRequestActivityProps) => {
  const { t } = useTranslation('payments');
  const [isExpanded, setIsExpanded] = useState(false);

  const { invoiceHistory, isLoading } = useRequestActivityQuery({
    invoiceId: invoiceId,
    locationId: locationId,
  });

  const visibleSortedActions = useMemo(() => {
    return (invoiceHistory || [])
      .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
      .filter((action) => action.actionType !== 'ACTION_UPDATED');
  }, [invoiceHistory]);

  const displayedActions = useMemo(() => {
    if (isExpanded || !maxVisibleActivities) {
      return visibleSortedActions;
    }
    return visibleSortedActions.slice(0, maxVisibleActivities);
  }, [visibleSortedActions, isExpanded, maxVisibleActivities]);

  const shouldShowViewMore = maxVisibleActivities && visibleSortedActions.length > maxVisibleActivities;

  if (isLoading) {
    return <SkeletonLoader width='100%' height='20px' count={2} distance={20} animation='shimmer' />;
  }

  return (
    <div className='payment-request-activity'>
      {visibleSortedActions.length ? (
        <>
          {displayedActions.map((action) => (
            <ActivityRow
              action={action}
              key={action.id}
              compactView={!!compactView}
              userChipAtBottom={userChipAtBottom}
            />
          ))}
          {shouldShowViewMore && !isExpanded && (
            <Button
              className='payment-request-activity__view-more'
              onClick={() => setIsExpanded(true)}
              variant='tertiary'
              trackingId='payment-request-activity-view-all'
            >
              {t('View All')}
            </Button>
          )}
        </>
      ) : (
        <Text>{t('No history to display')}</Text>
      )}
    </div>
  );
};
